import { BorderGlow, Button } from "@/components/ui";
import { useFocusChromeAutoHide } from "@/components/layout/focus-chrome-autohide-store";
import { useDenkerActivityActive } from "@/hooks/use-denker-activity-active";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { layoutMessages } from "@/i18n/messages/layout";
import "./focus-chrome-autohide.css";

type FocusChromeHandleSide = "top" | "left";

function FocusChromeRevealHandle({
  className,
  side,
}: {
  className?: string;
  side: FocusChromeHandleSide;
}) {
  const t = useT(layoutMessages);
  const { revealFocusChrome } = useFocusChromeAutoHide();
  const denkerActive = useDenkerActivityActive();
  const vertical = side === "left";

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={t("showFocusControls")}
      className={cn(
        "denker-focus-chrome-reveal-handle group relative pointer-events-auto flex bg-transparent p-0 opacity-80 transition-[opacity,transform] duration-200 ease-out hover:bg-transparent hover:opacity-100 focus-visible:opacity-100",
        vertical ? "h-28 w-5" : "h-7 w-28",
        className,
      )}
      data-agent-active={denkerActive ? "true" : undefined}
      data-interactive="true"
      data-testid={`focus-chrome-${side}-reveal`}
      onFocus={revealFocusChrome}
      onPointerEnter={revealFocusChrome}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        revealFocusChrome();
      }}
    >
      <BorderGlow
        as="span"
        aria-hidden="true"
        className={cn(
          "denker-focus-chrome-reveal-body absolute block overflow-visible border transition-[opacity,transform] duration-200 ease-out group-hover:scale-110",
          vertical
            ? "left-0 top-1/2 h-14 w-[14px] -translate-y-1/2"
            : "left-1/2 top-0 h-[14px] w-12 -translate-x-1/2",
        )}
        active={denkerActive}
        animated={denkerActive}
        activeOnHover={!denkerActive}
        animationDuration={denkerActive ? 2.25 : 3.2}
        borderWidth={denkerActive ? 1.5 : 1}
        glowIntensity={denkerActive ? 1 : 0.58}
        glowRadius={vertical ? 18 : 20}
        ringOffset={denkerActive ? 3 : 1}
        data-side={side}
        data-agent-active={denkerActive ? "true" : undefined}
        data-testid={`focus-chrome-${side}-reveal-body`}
      />
    </Button>
  );
}

export { FocusChromeRevealHandle };
